'use strict';

const libPictProvider = require('pict-provider');
const libCatalog = require('./ATMDashboardConfig-Catalog.js');

const DETAIL_MOUNT = '#ATM-Detail';
const PROJECT_RESULTS_MOUNT = '#ATM-Project-Results';

const NS_AUDIT = libCatalog.MATERIALS_AUDIT_NAMESPACE;      // 'MaterialsAudit'
const NS_DASH = libCatalog.APPDASHBOARDS_NAMESPACE;         // 'AppDashboards'

/**
 * ATMDashboardConfig — the bespoke #/ATMDashboardConfig screen. Edits the Audit of Testing & Materials /
 * Sample Plan Checklist dashboard configuration across its two real config surfaces, at Customer default or
 * per-Project override scope, gated by the `MaterialsAudit-Configure` entitlement.
 *
 *   Validation (namespace MaterialsAudit, one NamespaceValue per top-level key) — the audit checks + pass/fail
 *     vocabulary; read by the API as Object.assign(defaults, Configuration/For/MaterialsAudit/In/Project/<id>).
 *   Display   (namespace AppDashboards, bucket `SamplePlan` → SamplePlanChecklist.Config.SamplePlan) — which
 *     checklist columns show + their labels; the bucket value is preserved and only its SamplePlan slice rewritten.
 *
 * Writes go through the same Configuration/Put path the ConfigViewer / ApprovalWorkflow tools use; the server gate
 * on Configuration/Put stays authoritative.
 */
class ATMDashboardConfigProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._searchTimer = null;
		this._searchToken = 0;
		this._displayBucketName = libCatalog.DISPLAY_BUCKET;
		this._displayBucketCfg = null;   // the full effective bucket object, preserved for save
	}

	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }

	get state()
	{
		if (!this.pict.AppData.ATMDashboardConfig)
		{
			this.pict.AppData.ATMDashboardConfig =
			{
				Loaded: false, Loading: false, Saving: false, Error: '', Message: '',
				ScopeMode: 'Customer', IDProject: 0, ProjectLabel: '', ProjectSearch: '', ProjectResults: [], ProjectSearching: false,
				ActiveTab: 'validation',
				ValChecks: {}, ValAccepted: {}, ValStatusPassing: '', ValStatusFailing: '', ValCert: '',
				DispSection: {}, DispColumns: {}, DisplayAvailable: true,
			};
		}
		return this.pict.AppData.ATMDashboardConfig;
	}

	callerCustomer() { const tmpUser = this.pict.AppData.CurrentUser; return tmpUser ? Number(tmpUser.CustomerID) : 0; }

	/** The entitlement gate — `MaterialsAudit-Configure`. Same capability the nav entry checks; server stays authoritative. */
	can()
	{
		const tmpSvc = this.pict.fable.EffectivePermissionsService || (this.fable && this.fable.EffectivePermissionsService);
		if (!tmpSvc || (typeof tmpSvc.can !== 'function')) { return true; }   // fail-open until the cap map loads
		return tmpSvc.can('MaterialsAudit', 'Configure');
	}

	_getJSON(pURL) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.getJSON(pURL, (pErr, pBody) => resolve(pErr ? null : pBody))) : Promise.resolve(null); }
	_postJSON(pURL, pBody) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.postJSON(pURL, pBody, (pErr, pRes) => resolve({ error: pErr, result: pRes }))) : Promise.resolve({ error: 'no client' }); }
	_esc(pStr) { return String(pStr == null ? '' : pStr).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar])); }

	// ── Config I/O (centralized so the storage shape lives in ONE place) ─────────

	_scope() { return (this.state.ScopeMode === 'Project') ? { Scope: 'Project', ID: Number(this.state.IDProject) } : { Scope: 'Customer', ID: this.callerCustomer() }; }

	/** Read a namespace's merged {NamespaceValue: config} map at a scope. */
	_readMap(pNamespace, pScope, pID) { return this._getJSON(`Configuration/For/${pNamespace}/In/${pScope}/${pID}`).then((pBody) => (pBody && typeof pBody === 'object' && !Array.isArray(pBody)) ? pBody : {}); }

	/** Write one NamespaceValue's config at a scope (server dedups a value equal to its parent). */
	_putValue(pNamespace, pValue, pScope, pID, pConfig) { return this._postJSON(`Configuration/Put/${pNamespace}/${pValue}/${pScope}/${pID}`, { Config: pConfig }); }

	// ── Load ─────────────────────────────────────────────────────────────────────

	loadAsync()
	{
		const tmpState = this.state;
		if (tmpState.Loading) { return; }
		if ((tmpState.ScopeMode === 'Project') && !tmpState.IDProject) { tmpState.Loaded = true; this.render(); return; }
		tmpState.Loading = true;
		tmpState.Error = '';
		this.render();
		const tmpScope = this._scope();
		Promise.all([ this._loadValidation(tmpScope), this._loadDisplay(tmpScope) ]).then(() =>
		{
			tmpState.Loaded = true;
			tmpState.Loading = false;
			this.render();
		});
	}

	_loadValidation(pScope)
	{
		const tmpState = this.state;
		return this._readMap(NS_AUDIT, pScope.Scope, pScope.ID).then((pMap) =>
		{
			tmpState.ValChecks = {};
			libCatalog.VALIDATION_CHECKS.forEach((pCheck) =>
			{
				const tmpVal = pMap[pCheck.Key];
				tmpState.ValChecks[pCheck.Key] = (tmpVal && (tmpVal.Enabled != null)) ? !!tmpVal.Enabled : pCheck.Default;
			});
			const tmpAccepted = pMap[libCatalog.ACCEPTED_QTY_KEY] || {};
			tmpState.ValAccepted =
			{
				Enabled: (tmpAccepted.Enabled != null) ? !!tmpAccepted.Enabled : libCatalog.ACCEPTED_QTY_DEFAULT.Enabled,
				ValidationField: tmpAccepted.ValidationField || libCatalog.ACCEPTED_QTY_DEFAULT.ValidationField,
			};
			const tmpStatus = pMap[libCatalog.STATUS_OPTIONS_KEY] || {};
			tmpState.ValStatusPassing = this._listToText(Array.isArray(tmpStatus.Passing) ? tmpStatus.Passing : libCatalog.STATUS_OPTIONS_DEFAULT.Passing);
			tmpState.ValStatusFailing = this._listToText(Array.isArray(tmpStatus.Failing) ? tmpStatus.Failing : libCatalog.STATUS_OPTIONS_DEFAULT.Failing);
			const tmpCert = pMap[libCatalog.CERT_UNIT_TYPES_KEY];
			tmpState.ValCert = this._linesToText(Array.isArray(tmpCert) ? tmpCert : libCatalog.CERT_UNIT_TYPES_DEFAULT);
		});
	}

	_loadDisplay(pScope)
	{
		const tmpState = this.state;
		return this._readMap(NS_DASH, pScope.Scope, pScope.ID).then((pMap) =>
		{
			// Pick the bucket the Sample Plan Checklist reads: SamplePlan, else HeadLight, else the first bucket that
			// carries a SamplePlanChecklist config. Preserve the whole bucket object so save only rewrites its slice.
			const tmpBucketName = pMap[libCatalog.DISPLAY_BUCKET] ? libCatalog.DISPLAY_BUCKET
				: (pMap.HeadLight && pMap.HeadLight.SamplePlanChecklist) ? 'HeadLight'
					: Object.keys(pMap).find((pKey) => pMap[pKey] && pMap[pKey].SamplePlanChecklist) || libCatalog.DISPLAY_BUCKET;
			const tmpBucket = (pMap[tmpBucketName] && pMap[tmpBucketName].SamplePlanChecklist) ? pMap[tmpBucketName] : null;
			// No SamplePlan bucket at this scope (a customer that hasn't configured the dashboard) — seed the canonical
			// one so columns are still configurable; saving then registers the dashboard for this scope.
			this._displayBucketName = libCatalog.DISPLAY_BUCKET;
			this._displayBucketIsNew = !tmpBucket;
			this._displayBucketCfg = tmpBucket ? JSON.parse(JSON.stringify(tmpBucket)) : JSON.parse(JSON.stringify(libCatalog.DISPLAY_BUCKET_TEMPLATE));
			tmpState.DisplayAvailable = true;
			tmpState.DisplayIsNew = !tmpBucket;
			const tmpSP = (this._displayBucketCfg.SamplePlanChecklist && this._displayBucketCfg.SamplePlanChecklist.Config && this._displayBucketCfg.SamplePlanChecklist.Config.SamplePlan) || null;
			// Overlay precedence: an existing SamplePlan config REPLACES the built-in fallback (spec §4.2); with no
			// config, seed from the fallback so the editor mirrors what the dashboard currently renders.
			const tmpOverlay = tmpSP || libCatalog.DISPLAY_FALLBACK_PROFILE;
			tmpState.DispSection = {};
			libCatalog.DISPLAY_SECTION_OPTIONS.forEach((pOpt) =>
			{
				tmpState.DispSection[pOpt.Key] = (tmpOverlay[pOpt.Key] != null) ? tmpOverlay[pOpt.Key] : pOpt.Default;
			});
			tmpState.DispColumns = {};
			libCatalog.DISPLAY_COLUMNS.forEach((pCol) =>
			{
				const tmpEntry = tmpOverlay[pCol.Key] || {};
				const tmpHidden = (tmpEntry.hidden != null) ? !!tmpEntry.hidden : !!pCol.Hidden;
				tmpState.DispColumns[pCol.Key] = { show: !tmpHidden, label: (tmpEntry.label != null) ? String(tmpEntry.label) : '' };
			});
		});
	}

	_listToText(pArr) { return (Array.isArray(pArr) ? pArr : []).join(', '); }
	_linesToText(pArr) { return (Array.isArray(pArr) ? pArr : []).join('\n'); }
	_parseList(pText) { return String(pText || '').split(',').map((pV) => pV.trim()).filter((pV) => pV.length > 0); }
	_parseLines(pText) { return String(pText || '').split(/\r?\n/).map((pV) => pV.trim()).filter((pV) => pV.length > 0); }

	// ── Scope ────────────────────────────────────────────────────────────────────

	setScopeMode(pMode)
	{
		const tmpState = this.state;
		const tmpMode = (pMode === 'Project') ? 'Project' : 'Customer';
		if (tmpState.ScopeMode === tmpMode) { return; }
		tmpState.ScopeMode = tmpMode;
		tmpState.Error = ''; tmpState.Message = '';
		tmpState.Loaded = false;
		if (tmpMode === 'Customer') { this.loadAsync(); }
		else if (tmpState.IDProject) { this.loadAsync(); }
		else { this.render(); }   // Project mode with no project chosen yet → prompt to pick one
	}

	setProjectSearch(pValue)
	{
		const tmpState = this.state;
		tmpState.ProjectSearch = String(pValue == null ? '' : pValue);
		if (this._searchTimer) { clearTimeout(this._searchTimer); }
		this._searchTimer = setTimeout(() => { this._searchTimer = null; this._searchProjects(); }, 250);
	}

	_searchProjects()
	{
		const tmpState = this.state;
		const tmpQuery = String(tmpState.ProjectSearch || '').trim();
		if (tmpQuery.length < 2) { tmpState.ProjectSearching = false; tmpState.ProjectResults = []; this._renderProjectResults(); return; }
		const tmpToken = ++this._searchToken;
		tmpState.ProjectSearching = true;
		this._renderProjectResults();
		const tmpLike = encodeURIComponent(`%${tmpQuery}%`);
		const tmpCustomer = this.callerCustomer();
		// Project search, scoped to this customer, matched on name / PO number (the Project columns the picker uses).
		const tmpGroup = `FOP~0~(~0~FBV~Name~LK~${tmpLike}~FBVOR~PurchaseOrderNumber~LK~${tmpLike}~FCP~0~)~0`;
		this._getJSON(`Projects/FilteredTo/FBV~IDCustomer~EQ~${tmpCustomer}~FBV~Deleted~EQ~0~${tmpGroup}~FSF~Name~ASC~0/0/25`).then((pRows) =>
		{
			if (tmpToken !== this._searchToken) { return; }
			tmpState.ProjectResults = Array.isArray(pRows) ? pRows : [];
			tmpState.ProjectSearching = false;
			this._renderProjectResults();
		});
	}

	selectProject(pID, pLabel)
	{
		const tmpState = this.state;
		tmpState.IDProject = Number(pID);
		tmpState.ProjectLabel = String(pLabel || `Project #${pID}`);
		tmpState.ProjectResults = [];
		tmpState.ProjectSearch = '';
		tmpState.Error = ''; tmpState.Message = '';
		tmpState.Loaded = false;
		this.loadAsync();
	}

	// ── Edits (validation) ───────────────────────────────────────────────────────

	toggleCheck(pKey) { const tmpState = this.state; tmpState.ValChecks[pKey] = !tmpState.ValChecks[pKey]; tmpState.Message = ''; this.render(); }
	toggleAcceptedEnabled() { const tmpState = this.state; tmpState.ValAccepted.Enabled = !tmpState.ValAccepted.Enabled; tmpState.Message = ''; this.render(); }
	setAcceptedField(pValue) { this.state.ValAccepted.ValidationField = String(pValue || 'ContractorReportedQuantity'); this.state.Message = ''; }
	setStatusPassing(pValue) { this.state.ValStatusPassing = String(pValue == null ? '' : pValue); this.state.Message = ''; }
	setStatusFailing(pValue) { this.state.ValStatusFailing = String(pValue == null ? '' : pValue); this.state.Message = ''; }
	setCert(pValue) { this.state.ValCert = String(pValue == null ? '' : pValue); this.state.Message = ''; }

	// ── Edits (display) ──────────────────────────────────────────────────────────

	toggleColumn(pKey) { const tmpState = this.state; const tmpCol = tmpState.DispColumns[pKey]; if (tmpCol) { tmpCol.show = !tmpCol.show; tmpState.Message = ''; this.render(); } }
	setColumnLabel(pKey, pValue) { const tmpCol = this.state.DispColumns[pKey]; if (tmpCol) { tmpCol.label = String(pValue == null ? '' : pValue); this.state.Message = ''; } }
	setSectionText(pKey, pValue) { this.state.DispSection[pKey] = String(pValue == null ? '' : pValue); this.state.Message = ''; }
	toggleSectionBool(pKey) { const tmpState = this.state; tmpState.DispSection[pKey] = !tmpState.DispSection[pKey]; tmpState.Message = ''; this.render(); }

	/** Switch tabs via a stable route (#/ATMDashboardConfig/<Tab>) so the active tab lives in the URL. (The
	 *  orthogonal Customer/Project ScopeMode stays in-place JS — it's a scope filter, not page navigation.) */
	navigateTab(pTab) { if ((typeof window !== 'undefined') && window.location) { window.location.hash = `#/ATMDashboardConfig/${ (pTab === 'display') ? 'display' : 'validation' }`; } else { this.setTab(pTab); } }

	setTab(pTab) { const tmpTab = (pTab === 'display') ? 'display' : 'validation'; if (this.state.ActiveTab === tmpTab) { return; } this.state.ActiveTab = tmpTab; this.state.Error = ''; this.render(); }

	// ── Save ─────────────────────────────────────────────────────────────────────

	async saveValidationAsync()
	{
		const tmpState = this.state;
		if (!this.can() || tmpState.Saving) { return; }
		if ((tmpState.ScopeMode === 'Project') && !tmpState.IDProject) { return; }
		tmpState.Saving = true; tmpState.Error = ''; tmpState.Message = '';
		this.render();
		const tmpScope = this._scope();
		const tmpWrites = [];
		libCatalog.VALIDATION_CHECKS.forEach((pCheck) => tmpWrites.push(this._putValue(NS_AUDIT, pCheck.Key, tmpScope.Scope, tmpScope.ID, { Enabled: !!tmpState.ValChecks[pCheck.Key] })));
		tmpWrites.push(this._putValue(NS_AUDIT, libCatalog.ACCEPTED_QTY_KEY, tmpScope.Scope, tmpScope.ID, { Enabled: !!tmpState.ValAccepted.Enabled, ValidationField: tmpState.ValAccepted.ValidationField }));
		tmpWrites.push(this._putValue(NS_AUDIT, libCatalog.STATUS_OPTIONS_KEY, tmpScope.Scope, tmpScope.ID, { Passing: this._parseList(tmpState.ValStatusPassing), Failing: this._parseList(tmpState.ValStatusFailing) }));
		tmpWrites.push(this._putValue(NS_AUDIT, libCatalog.CERT_UNIT_TYPES_KEY, tmpScope.Scope, tmpScope.ID, this._parseLines(tmpState.ValCert)));
		const tmpResults = await Promise.all(tmpWrites);
		tmpState.Saving = false;
		const tmpFailed = tmpResults.filter((pR) => pR && pR.error).length;
		if (tmpFailed) { tmpState.Error = `Saved with ${tmpFailed} error(s) — the server may have rejected a write (you may lack config-admin rights in this scope).`; }
		else { tmpState.Message = `Validation rules saved to the ${this._scopeLabel()}.`; }
		this.render();
	}

	async saveDisplayAsync()
	{
		const tmpState = this.state;
		if (!this.can() || tmpState.Saving) { return; }
		if ((tmpState.ScopeMode === 'Project') && !tmpState.IDProject) { return; }
		if (!tmpState.DisplayAvailable || !this._displayBucketCfg) { tmpState.Error = 'No Sample Plan Checklist dashboard is configured to edit its columns against.'; this.render(); return; }
		tmpState.Saving = true; tmpState.Error = ''; tmpState.Message = '';
		this.render();
		// Build the full SamplePlan slice from the editor (explicit + lossless: an override replaces the fallback).
		const tmpSP = {};
		libCatalog.DISPLAY_SECTION_OPTIONS.forEach((pOpt) => { tmpSP[pOpt.Key] = tmpState.DispSection[pOpt.Key]; });
		libCatalog.DISPLAY_COLUMNS.forEach((pCol) =>
		{
			const tmpCol = tmpState.DispColumns[pCol.Key] || { show: !pCol.Hidden, label: '' };
			const tmpEntry = { hidden: !tmpCol.show };
			if (tmpCol.label && tmpCol.label.length) { tmpEntry.label = tmpCol.label; }
			tmpSP[pCol.Key] = tmpEntry;
		});
		// Splice into a clone of the preserved bucket so DashboardHash / EntryPoints / other keys survive.
		const tmpBucket = JSON.parse(JSON.stringify(this._displayBucketCfg));
		if (!tmpBucket.SamplePlanChecklist) { tmpBucket.SamplePlanChecklist = {}; }
		if (!tmpBucket.SamplePlanChecklist.Config) { tmpBucket.SamplePlanChecklist.Config = {}; }
		tmpBucket.SamplePlanChecklist.Config.SamplePlan = tmpSP;
		const tmpScope = this._scope();
		const tmpRes = await this._putValue(NS_DASH, this._displayBucketName, tmpScope.Scope, tmpScope.ID, tmpBucket);
		tmpState.Saving = false;
		if (tmpRes && tmpRes.error) { tmpState.Error = 'The server rejected the display config write (you may lack config-admin rights in this scope). Nothing was changed.'; }
		else { tmpState.Message = `Display columns saved to the ${this._scopeLabel()}.`; this._displayBucketCfg = tmpBucket; }
		this.render();
	}

	_scopeLabel() { return (this.state.ScopeMode === 'Project') ? `project “${this.state.ProjectLabel}”` : 'customer default'; }

	// ── Render ─────────────────────────────────────────────────────────────────

	render()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpCA || (tmpCA.getElement(DETAIL_MOUNT).length === 0)) { return; }
		tmpCA.assignContent(DETAIL_MOUNT, this._detailHTML());
	}

	_renderProjectResults()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement(PROJECT_RESULTS_MOUNT).length > 0) { tmpCA.assignContent(PROJECT_RESULTS_MOUNT, this._projectResultsHTML()); }
		else { this.render(); }
	}

	_detailHTML()
	{
		const tmpState = this.state;
		if (!this.can()) { return '<div class="atm-empty">You don’t have the “Configure the Audit of Testing and Materials dashboard” entitlement.</div>'; }
		const tmpScopeBar = this._scopeBarHTML();
		if ((tmpState.ScopeMode === 'Project') && !tmpState.IDProject)
		{
			return tmpScopeBar + '<div class="atm-empty">Search for and pick a project above to configure its override.</div>';
		}
		if (tmpState.Loading || !tmpState.Loaded) { return tmpScopeBar + '<div class="atm-empty">Loading configuration…</div>'; }
		const tmpMsg = tmpState.Message ? `<div class="atm-msg">${this._esc(tmpState.Message)}</div>` : '';
		const tmpErr = tmpState.Error ? `<div class="atm-error">${this._esc(tmpState.Error)}</div>` : '';
		const tmpBody = (tmpState.ActiveTab === 'display') ? this._displayHTML() : this._validationHTML();
		return tmpScopeBar + this._tabBarHTML() + tmpErr + tmpMsg + tmpBody;
	}

	_scopeBarHTML()
	{
		const tmpState = this.state;
		const tmpIsProject = (tmpState.ScopeMode === 'Project');
		const tmpProjectPicker = tmpIsProject
			? `<div class="atm-project"><input class="atm-project-input" type="text" autocomplete="off" spellcheck="false" placeholder="Search projects by name or number…" value="${this._esc(tmpState.ProjectSearch || '')}" oninput="_Pict.providers.ATMDashboardConfig.setProjectSearch(this.value)" />`
				+ (tmpState.IDProject ? `<span class="atm-project-current">Editing: <strong>${this._esc(tmpState.ProjectLabel)}</strong></span>` : '')
				+ `<div id="ATM-Project-Results" class="atm-project-results">${this._projectResultsHTML()}</div></div>`
			: '';
		return `<div class="atm-scopebar">
				<span class="atm-scope-label">Scope</span>
				<button type="button" class="atm-scope-btn${tmpIsProject ? '' : ' is-active'}" onclick="_Pict.providers.ATMDashboardConfig.setScopeMode('Customer'); return false;">Customer default</button>
				<button type="button" class="atm-scope-btn${tmpIsProject ? ' is-active' : ''}" onclick="_Pict.providers.ATMDashboardConfig.setScopeMode('Project'); return false;">Project override</button>
			</div>${tmpProjectPicker}`;
	}

	_projectResultsHTML()
	{
		const tmpState = this.state;
		if (tmpState.ProjectSearching) { return '<div class="atm-project-empty">Searching…</div>'; }
		const tmpQuery = String(tmpState.ProjectSearch || '').trim();
		if (tmpQuery.length < 2) { return ''; }
		if (!(tmpState.ProjectResults || []).length) { return `<div class="atm-project-empty">No projects match “${this._esc(tmpQuery)}”.</div>`; }
		return (tmpState.ProjectResults || []).map((pRow) =>
		{
			const tmpLabel = pRow.Name || `Project #${pRow.IDProject}`;
			const tmpSub = pRow.PurchaseOrderNumber || pRow.ImprovementTypeCode || '';
			return `<div class="atm-project-item" onclick="_Pict.providers.ATMDashboardConfig.selectProject(${Number(pRow.IDProject)}, '${this._esc(String(tmpLabel).replace(/'/g, ''))}'); return false;">${this._esc(tmpLabel)}${tmpSub ? `<span class="atm-project-sub">${this._esc(tmpSub)}</span>` : ''}</div>`;
		}).join('');
	}

	_tabBarHTML()
	{
		const tmpTab = this.state.ActiveTab || 'validation';
		return `<div class="atm-tabs">
				<button type="button" class="atm-tab${tmpTab === 'validation' ? ' is-active' : ''}" onclick="_Pict.providers.ATMDashboardConfig.navigateTab('validation'); return false;">Validation rules</button>
				<button type="button" class="atm-tab${tmpTab === 'display' ? ' is-active' : ''}" onclick="_Pict.providers.ATMDashboardConfig.navigateTab('display'); return false;">Display columns</button>
			</div>`;
	}

	_switch(pOn, pOnClick, pDisabled) { return `<button type="button" class="atm-switch${pOn ? ' is-on' : ''}"${pDisabled ? ' disabled' : ''} onclick="${pOnClick}; return false;"><span class="atm-switch-dot"></span></button>`; }

	_validationHTML()
	{
		const tmpState = this.state;
		const tmpDisabled = tmpState.Saving;
		const tmpChecks = libCatalog.VALIDATION_CHECKS.map((pCheck) =>
			`<div class="atm-row"><div class="atm-row-main"><div class="atm-row-label">${this._esc(pCheck.Label)}</div><div class="atm-row-desc">${this._esc(pCheck.Description)}</div></div>${this._switch(!!tmpState.ValChecks[pCheck.Key], `_Pict.providers.ATMDashboardConfig.toggleCheck('${pCheck.Key}')`, tmpDisabled)}</div>`).join('');
		const tmpAcceptedOn = !!tmpState.ValAccepted.Enabled;
		const tmpFieldOpts = libCatalog.ACCEPTED_QTY_FIELDS.map((pF) => `<option value="${pF.Value}"${tmpState.ValAccepted.ValidationField === pF.Value ? ' selected' : ''}>${this._esc(pF.Label)}</option>`).join('');
		const tmpAccepted = `<div class="atm-row"><div class="atm-row-main"><div class="atm-row-label">Accepted-quantity check</div>`
			+ `<div class="atm-row-desc">Flag a line item when the accepted quantity falls short of the quantity that needed coverage.</div>`
			+ `<div class="atm-subfield"><label class="atm-sublabel">Measured against</label> <select class="atm-select"${tmpAcceptedOn ? '' : ' disabled'} onchange="_Pict.providers.ATMDashboardConfig.setAcceptedField(this.value)">${tmpFieldOpts}</select></div></div>`
			+ this._switch(tmpAcceptedOn, `_Pict.providers.ATMDashboardConfig.toggleAcceptedEnabled()`, tmpDisabled) + `</div>`;
		const tmpStatus = `<div class="atm-area"><div class="atm-area-head">Pass / fail vocabulary</div>`
			+ `<div class="atm-field"><label class="atm-field-label">Statuses that count as <strong>passing</strong> (comma-separated)</label>`
			+ `<input class="atm-input" type="text" value="${this._esc(tmpState.ValStatusPassing)}" oninput="_Pict.providers.ATMDashboardConfig.setStatusPassing(this.value)" /></div>`
			+ `<div class="atm-field"><label class="atm-field-label">Statuses that count as <strong>failing</strong> (comma-separated)</label>`
			+ `<input class="atm-input" type="text" value="${this._esc(tmpState.ValStatusFailing)}" oninput="_Pict.providers.ATMDashboardConfig.setStatusFailing(this.value)" /></div></div>`;
		const tmpCert = `<div class="atm-area"><div class="atm-area-head">Certification-style unit types</div>`
			+ `<div class="atm-field"><label class="atm-field-label">PLIMT unit strings treated as certification testing (one per line)</label>`
			+ `<textarea class="atm-textarea" rows="4" oninput="_Pict.providers.ATMDashboardConfig.setCert(this.value)">${this._esc(tmpState.ValCert)}</textarea></div></div>`;
		return `<div class="atm-body">
				<div class="atm-area"><div class="atm-area-head">Conformance checks</div>${tmpChecks}${tmpAccepted}</div>
				${tmpStatus}${tmpCert}
				${this._saveBarHTML('saveValidationAsync', 'Save validation rules')}
			</div>`;
	}

	_displayHTML()
	{
		const tmpState = this.state;
		const tmpNewNote = tmpState.DisplayIsNew
			? `<div class="atm-note atm-note-warn">No Sample Plan Checklist dashboard config exists at the ${this._esc(this._scopeLabel())} yet — saving here will create it (registering the dashboard for this scope) with the columns below.</div>`
			: '';
		const tmpDisabled = tmpState.Saving;
		const tmpSection = libCatalog.DISPLAY_SECTION_OPTIONS.map((pOpt) =>
		{
			if (pOpt.Type === 'bool')
			{
				return `<div class="atm-row"><div class="atm-row-main"><div class="atm-row-label">${this._esc(pOpt.Label)}</div></div>${this._switch(!!tmpState.DispSection[pOpt.Key], `_Pict.providers.ATMDashboardConfig.toggleSectionBool('${pOpt.Key}')`, tmpDisabled)}</div>`;
			}
			return `<div class="atm-field"><label class="atm-field-label">${this._esc(pOpt.Label)}</label><input class="atm-input" type="text" value="${this._esc(tmpState.DispSection[pOpt.Key] || '')}" oninput="_Pict.providers.ATMDashboardConfig.setSectionText('${pOpt.Key}', this.value)" /></div>`;
		}).join('');
		const tmpGroups = libCatalog.DISPLAY_COLUMN_GROUPS.map((pGroup) =>
		{
			const tmpRows = pGroup.Columns.map((pCol) =>
			{
				const tmpCol = tmpState.DispColumns[pCol.Key] || { show: !pCol.Hidden, label: '' };
				const tmpToggle = this._switch(!!tmpCol.show, `_Pict.providers.ATMDashboardConfig.toggleColumn('${pCol.Key}')`, tmpDisabled);
				const tmpDefaultLabel = String(pCol.Label).replace(/<br\s*\/?>/gi, ' ');
				return `<div class="atm-col"><div class="atm-col-main"><span class="atm-col-name">${this._esc(tmpDefaultLabel)}</span>`
					+ `<input class="atm-col-label" type="text" placeholder="${this._esc(tmpDefaultLabel)}" value="${this._esc(tmpCol.label)}" oninput="_Pict.providers.ATMDashboardConfig.setColumnLabel('${pCol.Key}', this.value)" /></div>`
					+ `<span class="atm-col-show">Show ${tmpToggle}</span></div>`;
			}).join('');
			return `<div class="atm-area"><div class="atm-area-head">${this._esc(pGroup.Group)}</div>${tmpRows}</div>`;
		}).join('');
		return `<div class="atm-body">
				${tmpNewNote}
				<div class="atm-note">Uncheck a column to hide it on the checklist; type a label to rename its header (blank = default). Editing scope: <strong>${this._esc(this._scopeLabel())}</strong>.</div>
				<div class="atm-area"><div class="atm-area-head">Section</div>${tmpSection}</div>
				${tmpGroups}
				${this._saveBarHTML('saveDisplayAsync', 'Save display columns')}
			</div>`;
	}

	_saveBarHTML(pMethod, pLabel)
	{
		const tmpState = this.state;
		const tmpDisabled = tmpState.Saving ? ' disabled' : '';
		return `<div class="atm-savebar"><button type="button" class="atm-save"${tmpDisabled} onclick="_Pict.providers.ATMDashboardConfig.${pMethod}(); return false;">${tmpState.Saving ? 'Saving…' : this._esc(pLabel)}</button></div>`;
	}
}

module.exports = ATMDashboardConfigProvider;
module.exports.default_configuration =
{
	ProviderIdentifier: 'ATMDashboardConfig',
	AutoInitialize: true,
	AutoInitializeOrdinal: 45,
};
